import { appPaths, stockMarketQuizRoute } from "../routes.config";
import { escapeXml, isExternalSource } from "./routeUtils";
import { pageShellClassName } from "./pageLayout";
import { quizContent, quizQuestions } from "./quizData";

export type StockMarketQuizSeoMetadata = {
  title: string;
  description: string;
  canonicalPath: string;
  structuredData: Record<string, unknown>;
};

export function getStockMarketQuizSeoMetadata(canonicalUrl: string, homeUrl: string): StockMarketQuizSeoMetadata | null {
  if (!stockMarketQuizRoute) return null;
  return {
    title: stockMarketQuizRoute.title,
    description: stockMarketQuizRoute.description,
    canonicalPath: stockMarketQuizRoute.path,
    structuredData: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: stockMarketQuizRoute.structuredName,
      description: stockMarketQuizRoute.description,
      url: canonicalUrl,
      inLanguage: "en-US",
      about: { "@type": "Thing", name: "Stock market education" },
      isPartOf: { "@type": "WebApplication", name: "Equity Replay", url: homeUrl },
      breadcrumb: { "@type": "BreadcrumbList", itemListElement: [{ "@type": "ListItem", position: 1, name: "Home", item: homeUrl }, { "@type": "ListItem", position: 2, name: "Stock market quiz", item: canonicalUrl }] },
    },
  };
}

export function renderStockMarketQuizContent(currentOrigin: string) {
  if (!stockMarketQuizRoute) return "";
  const topics = [...new Set(quizQuestions.map((question) => question.topic))].map((topic) => `<li><strong>${escapeXml(topic)}.</strong> Concepts explained in plain language.</li>`).join("");
  const sources = [...new Map(quizQuestions.map((question) => [question.sourceUrl, question.sourceLabel] as const))].map(([url, label]) => `<li><a href="${escapeXml(url)}"${isExternalSource(url, currentOrigin) ? ' target="_blank" rel="noreferrer"' : ""}>${escapeXml(label)}</a></li>`).join("");
  const faqs = quizContent.faqs.map(({ question, answer }) => `<details><summary>${escapeXml(question)}</summary><p>${escapeXml(answer)}</p></details>`).join("");
  return `<main class="${pageShellClassName("seo-stock-market-quiz")}" aria-labelledby="stock-market-quiz-title">
    <nav aria-label="Breadcrumb"><a href="${escapeXml(appPaths.home)}">Equity Replay</a> <span aria-hidden="true">/</span> <span>Stock market quiz</span></nav>
    <article>
      <p class="section-label">${escapeXml(stockMarketQuizRoute.label)}</p>
      <h1 id="stock-market-quiz-title">${escapeXml(stockMarketQuizRoute.heading)}</h1>
      <p>${escapeXml(stockMarketQuizRoute.intro)}</p>
      <p><a class="primary-button" href="${escapeXml(stockMarketQuizRoute.path)}#quiz">Start the quiz <span aria-hidden="true">→</span></a></p>
      <section aria-labelledby="quiz-topics-heading">
        <p class="section-label">A quick knowledge check</p>
        <h2 id="quiz-topics-heading">What does this stock market quiz cover?</h2>
        <p>${escapeXml(quizContent.coverage)}</p>
        <ul>${topics}</ul>
      </section>
      <section aria-labelledby="quiz-faq-heading">
        <p class="section-label">Before you start</p>
        <h2 id="quiz-faq-heading">Public portfolio and stock market quiz questions</h2>
        ${faqs}
      </section>
      <section aria-labelledby="quiz-method-heading">
        <p class="section-label">How it works</p>
        <h2 id="quiz-method-heading">Learn, then put the vocabulary into context.</h2>
        <p>${escapeXml(quizContent.methodIntro)} When you are ready, <a href="${escapeXml(appPaths.explore)}">explore public portfolios</a>, <a href="${escapeXml(appPaths.blog)}/what-is-a-13f-filing">read about Form 13F</a>, <a href="${escapeXml(appPaths.buildPortfolio)}">build a portfolio</a>, read the <a href="${escapeXml(appPaths.methodology)}">historical replay methodology</a>, or explore the <a href="${escapeXml(appPaths.calculators)}">financial calculators</a>.</p>
        <ul class="quiz-source-list">${sources}</ul>
      </section>
      <p class="seo-stock-market-quiz-disclaimer">${escapeXml(quizContent.disclaimer)}</p>
    </article>
  </main>`;
}
